import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { snack } from '@innovamat/glimmer-components';
import { useCopyToClipboard } from '@innovamat/hooks';
import { Stage } from '@innovamat/radiance-utils';

import { useUser } from '../../../../user-management';
import type { ClassroomStudent } from '../types/classroom-student';

import {
  ActionsDropdown,
  type CreateItem,
} from '../../../components/common-table-components/actions-dropdown';
import { useOrganization } from '../../../hooks/use-organization';

type Props = {
  stage: string | null;
  student: ClassroomStudent | undefined;
  isJourneys: boolean;
  onChangePassword: (studentId: string) => void;
  onCompleteInvitation: (studentId: string) => void;
  onDeleteStudent: (student: ClassroomStudent | undefined) => void;
  onMoveStudent: (student: ClassroomStudent | undefined) => void;
  onMoveStudentToGroup: (student: ClassroomStudent | undefined) => void;
  onDeleteStudentFromGroup: (student: ClassroomStudent | undefined) => void;
  onResendInvitation: (studentId: string) => void;
  onResetTests: (student: ClassroomStudent | undefined) => void;
};

function ClassroomStudentsTableActionsCell({
  stage,
  student,
  isJourneys,
  onChangePassword,
  onCompleteInvitation,
  onDeleteStudent,
  onMoveStudent,
  onMoveStudentToGroup,
  onDeleteStudentFromGroup,
  onResendInvitation,
  onResetTests,
}: Props): JSX.Element | null {
  const { t } = useTranslation();
  const { isRole } = useUser();
  const [_, copy] = useCopyToClipboard();
  const { isRosteringEnabled } = useOrganization();

  const isSecondary = stage === Stage.SECONDARY || stage === Stage.SECUNDARIA;
  const isPrimaryWithEmail = stage === Stage.PRIMARIA && student?.email;

  const moveStudentItem: CreateItem = {
    text: t('students.button.moveClassroom'),
    icon: 'MoveIcon',
    state: student?.isDemo ? 'disabled' : 'active',
    onSelectItem: () => onMoveStudent(student),
  };

  const moveStudentToGroupItem: CreateItem = {
    text: t('students.button.moveGroup'),
    icon: 'MoveIcon',
    state: 'active',
    onSelectItem: () => onMoveStudentToGroup(student),
  };

  const deleteStudentItem: CreateItem = {
    text: t('students.button.removeStudent'),
    icon: 'DeleteIcon',
    state: 'active',
    onSelectItem: () => onDeleteStudent(student),
  };

  const deleteStudentFromGroupItem: CreateItem = {
    text: t('students.button.deleteFromGroup'),
    icon: 'DeleteIcon',
    state: 'active',
    onSelectItem: () => onDeleteStudentFromGroup(student),
  };

  const catchupItem: CreateItem = {
    text: t('students.button.resetcatchup'),
    icon: 'RestartIcon',
    state: 'active',
    onSelectItem: () => onResetTests(student),
  };

  const copyIdItem: CreateItem = {
    text: t('manager.table.copyId'),
    icon: 'CopyIcon',
    state: 'active',
    onSelectItem: () => {
      copy(student?.id || '');
      snack.success(t('manager.table.copiedId'));
    },
  };

  const resendInvitationItem: CreateItem = {
    text: t('common.resendInvitation'),
    icon: 'MailIcon',
    state: 'active',
    onSelectItem: () => onResendInvitation(student?.id!),
  };

  const completeInvitationItem: CreateItem = {
    text: t('students.completeManually'),
    icon: 'EditIcon',
    state: 'active',
    onSelectItem: () => onCompleteInvitation(student?.id!),
  };

  const changePasswordItem: CreateItem = {
    text: t('common.updatePassword'),
    icon: 'PasswordIcon',
    state: 'active',
    onSelectItem: () => onChangePassword(student?.id!),
  };

  const items: CreateItem[] = useMemo(() => {
    const baseItems: CreateItem[] =
      isRosteringEnabled || isJourneys
        ? []
        : [moveStudentItem, deleteStudentItem];

    if (isJourneys) {
      baseItems.push(moveStudentToGroupItem);
      baseItems.push(deleteStudentFromGroupItem);
    }

    if (isRole.Advisor) {
      baseItems.push(catchupItem);
      baseItems.push(copyIdItem);
    }

    if (isSecondary) {
      if (student?.isInvitation) {
        baseItems.push(resendInvitationItem);
        baseItems.push(completeInvitationItem);
      } else {
        if (!isRosteringEnabled) {
          baseItems.push(changePasswordItem);
        }
      }
    } else if (isPrimaryWithEmail) {
      if (!isRosteringEnabled) {
        baseItems.push(changePasswordItem);
      }
    }

    return baseItems;
  }, [isRosteringEnabled, onDeleteStudent, onMoveStudent, student, t]);

  if (items.length === 0) {
    return null;
  }

  return <ActionsDropdown dropdownId="school-students-button" items={items} />;
}

export { ClassroomStudentsTableActionsCell };
